import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const PORTRAIT = "/irina.jpeg";

const CHAPTERS = [
  { c: "Chapter I", t: "Two decades at the sharp end of ER", d: "I've spent 20+ years in senior HR and Employee Relations roles across the Middle East and international markets — leading HR governance across 12 countries and handling the complex, sensitive cases that rarely reach a job description." },
  { c: "Chapter II", t: "Where judgement matters most", d: "My work sits where policy meets people: workplace investigations, misconduct and grievance matters, senior-leader cases, sensitive exits and the escalation decisions that carry real risk. I simplify what has become complex — including disciplinary frameworks rebuilt for Dubai and Qatar." },
  { c: "Chapter III", t: "Independent, and discreet by design", d: "HR leaders and boards bring me in precisely because the matters are sensitive. Every engagement is handled independently, calmly and in strict confidence — evidence and experience, not marketing claims." },
];

const FACTS = [
  "20+ years senior HR & Employee Relations",
  "HR governance across 12 countries",
  "Complex investigations & sensitive cases",
  "People-risk & ER framework design",
  "Regulated, multi-jurisdiction environments",
  "Trusted adviser to senior leadership",
];

const STATS = [
  { v: "20+", l: "Years HR & ER" },
  { v: "12", l: "Countries" },
  { v: "Multi", l: "Market reach" },
];

export function Manifesto() {
  return (
    <section id="about" data-testid="about-section" className="py-28 px-6 md:px-12 bg-ink border-t border-white/10">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
        <div className="md:col-span-5">
          <div className="md:sticky md:top-28">
            <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} whileInView={{ clipPath: "inset(0% 0 0 0)" }} viewport={{ once: true }}
              transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }} className="relative overflow-hidden">
              <img src={PORTRAIT} alt="Irina Ishik, Employee Relations and People Risk advisor" data-testid="about-portrait"
                className="w-full max-h-[70vh] object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              <div className="absolute inset-0 bg-gold/10 mix-blend-overlay pointer-events-none" />
            </motion.div>
            <div className="grid grid-cols-3 gap-4 mt-8 border-t border-white/15 pt-8">
              {STATS.map((s) => (
                <div key={s.l} data-testid={`about-stat-${s.v}`}>
                  <p className="font-serif text-3xl md:text-4xl text-gold">{s.v}</p>
                  <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted2 mt-2">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="md:col-span-7">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.35em] text-gold uppercase mb-6">About Irina Ishik</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-8 max-w-xl">
              The senior ER professional HR leaders call when it's complex.
            </h2>
            <p className="text-muted2 leading-relaxed max-w-xl mb-10">
              An experienced Employee Relations and People Risk specialist you can call when a situation is complex,
              sensitive or high-risk — and who can also help your organisation prevent the same problems recurring by
              building stronger ER capability.
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-16 border-y border-white/15 py-8">
              {FACTS.map((f) => (
                <div key={f} className="flex items-start gap-3 text-bone/90 text-sm">
                  <span className="text-gold mt-1.5 h-1.5 w-1.5 rounded-full bg-gold shrink-0" /> {f}
                </div>
              ))}
            </div>
          </Reveal>

          <div className="space-y-14">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={ch.c} delay={i * 0.05}>
                <div className="border-l border-gold/40 pl-8" data-testid={`about-chapter-${i}`}>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-4">{ch.c}</p>
                  <h3 className="font-serif text-2xl md:text-3xl font-light mb-4">{ch.t}</h3>
                  <p className="text-muted2 leading-relaxed">{ch.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
